import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import axios from 'axios'
import AddIcon from '@mui/icons-material/Add'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import { toast } from 'react-toastify'
import Sidebar from 'components/Sidebar'
import CreateEventModal from 'components/events/CreateEventModal'
import { PageContainerMain } from 'pages/pages.styled'
import { getExploreEvents, setViewEventModal, toggleEventInterest } from 'features/events/eventSlice'
import { formatExploreDate, formatExploreLocation, formatExploreTime } from 'utils/exploreFormatters'
import { baseUrl } from 'features/constants'
import { coordinate } from 'utils/eventContract'
import { useTranslation } from 'react-i18next'
import { useAppLanguage } from 'i18n/useAppLanguage'

export default function Explore() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const locationInputRef = useRef(null)
  const { t } = useTranslation('explore')
  const { currentLocale } = useAppLanguage()
  const { user } = useSelector((state) => state.auth)
  const { profileChurch } = useSelector((state) => state.profileChurch)
  const { exploreEvents, isExploreLoading, isExploreError, exploreMessage, viewEventModal } = useSelector((state) => state.events)

  const [viewMode, setViewMode] = useState('discover')
  const [searchText, setSearchText] = useState('')
  const [radiusMiles, setRadiusMiles] = useState(50)
  const [eventFormat, setEventFormat] = useState('all')
  const [dateFilter, setDateFilter] = useState('any')
  const [locationSearch, setLocationSearch] = useState('')
  const [searchedLocation, setSearchedLocation] = useState(null)
  const [isSearchingLocation, setIsSearchingLocation] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [interestLoadingId, setInterestLoadingId] = useState(null)
  const [activeSection, setActiveSection] = useState('events')
  const [publicItems, setPublicItems] = useState([])
  const [isPublicLoading, setIsPublicLoading] = useState(false)
  const [publicError, setPublicError] = useState('')

  const savedCoordinates = useMemo(() => {
    const point = user?.marketLocation?.point?.coordinates || user?.marketLocation?.coordinates
    return Array.isArray(point) && point.length === 2 ? point : null
  }, [user])
  const coordinates = searchedLocation?.coordinates || savedCoordinates
  const canCreateEvent = Boolean(user?.isOrganization || profileChurch?._id)

  const visibleEvents = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    return exploreEvents.filter((item) => {
      const searchContent = [item.title, item.description, item.location?.label, item.organizer?.name]
        .filter(Boolean).join(' ').toLowerCase()
      return (!query || searchContent.includes(query))
        && (viewMode !== 'interested' || item.isInterested)
    })
  }, [exploreEvents, searchText, viewMode])

  const visiblePublicItems = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    if (!query) return publicItems
    return publicItems.filter((item) => [
      item.title,
      item.postText?.text,
      item.creator?.fullName,
    ].filter(Boolean).join(' ').toLowerCase().includes(query))
  }, [publicItems, searchText])

  const handleLocationSearch = async (event) => {
    event.preventDefault()
    if (!locationSearch.trim()) return
    setIsSearchingLocation(true)
    setLocationError('')
    try {
      const response = await axios.post(`${baseUrl}/api/maps/getCoordinates`, { address: locationSearch.trim() })
      const result = response.data?.data?.results?.[0]
      const lat = coordinate(result?.geometry?.location?.lat, 90)
      const lng = coordinate(result?.geometry?.location?.lng, 180)
      if (lat === null || lng === null) throw new Error('Location not found')
      setSearchedLocation({
        label: result.formatted_address || locationSearch.trim(),
        coordinates: [lng, lat],
      })
      setLocationSearch(result.formatted_address || locationSearch.trim())
    } catch {
      setLocationError('locationNotFound')
    } finally {
      setIsSearchingLocation(false)
    }
  }

  const handleInterest = async (event, item) => {
    event.stopPropagation()
    setInterestLoadingId(item.id)
    try {
      await dispatch(toggleEventInterest({ eventId: item.id })).unwrap()
    } catch (error) {
      toast.error(error || t('errors.interest'))
    } finally {
      setInterestLoadingId(null)
    }
  }

  const handleShare = async (event, item) => {
    event.stopPropagation()
    const url = `${window.location.origin}/events/${item.id}`
    try {
      if (navigator.share) {
        await navigator.share({ title: item.title, url })
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url)
        toast.success(t('notices.linkCopied'))
      }
    } catch (error) {
      if (error?.name !== 'AbortError') toast.error(t('errors.share'))
    }
  }

  const handleCreateEvent = () => {
    if (!canCreateEvent) {
      toast.info(t('errors.createRequiresChurch'))
      return
    }
    dispatch(setViewEventModal())
  }

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    if (activeSection !== 'events') return
    dispatch(getExploreEvents({
      radiusMiles,
      limit: 50,
      eventFormat,
      dateFilter,
      searchTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      interestedOnly: viewMode === 'interested',
      ...(eventFormat !== 'online' && coordinates ? { longitude: coordinates[0], latitude: coordinates[1] } : {}),
    }))
  }, [activeSection, coordinates, dateFilter, dispatch, eventFormat, navigate, radiusMiles, user, viewMode])

  useEffect(() => {
    if (!user || activeSection === 'events') return
    let cancelled = false

    const loadPublicContent = async () => {
      setIsPublicLoading(true)
      setPublicError('')
      try {
        const path = activeSection === 'posts'
          ? '/api/feed/explorePosts'
          : '/api/videos/explore'
        const body = { limit: 48 }
        const response = await axios.post(`${baseUrl}${path}`, body, {
          withCredentials: true,
          headers: user.token ? { Authorization: `Bearer ${user.token}` } : {},
        })
        if (!cancelled) {
          setPublicItems(activeSection === 'posts'
            ? (response.data?.items || [])
            : (response.data?.videos || []))
        }
      } catch (error) {
        if (!cancelled) {
          setPublicItems([])
          setPublicError(error.response?.data?.message || true)
        }
      } finally {
        if (!cancelled) setIsPublicLoading(false)
      }
    }

    loadPublicContent()
    return () => { cancelled = true }
  }, [activeSection, user])

  const sectionLabel = (section) => t(`sections.${section}`).toLocaleLowerCase(currentLocale)
  const dateOptions = ['any', 'today', 'tomorrow', 'week', 'weekend']

  return (
    <PageContainerMain>
      <Sidebar />
      <ExploreWorkspace>
        <ExploreHeader>
          <div><h1>{t('title')}</h1><p>{t('subtitle')}</p></div>
          <ExploreTabs aria-label={t('tabsLabel')}>
            <ExploreTab type='button' $active={activeSection === 'posts'} onClick={() => { setActiveSection('posts'); setSearchText('') }}><ArticleOutlinedIcon /> {t('sections.posts')}</ExploreTab>
            <ExploreTab type='button' $active={activeSection === 'events'} onClick={() => { setActiveSection('events'); setSearchText('') }}><EventAvailableOutlinedIcon /> {t('sections.events')}</ExploreTab>
            <ExploreTab type='button' $active={activeSection === 'media'} onClick={() => { setActiveSection('media'); setSearchText('') }}><VideoLibraryOutlinedIcon /> {t('sections.media')}</ExploreTab>
          </ExploreTabs>
        </ExploreHeader>

        <ExploreContentRow>
          {activeSection === 'events' ? (
            <>
              <ControlPanel>
                <PanelHeader><h1>{t('sections.events')}</h1><SearchWrap><SearchIcon /><input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder={t('search.events')} /></SearchWrap></PanelHeader>
                <PanelNav>
                  <PanelItem $active={viewMode === 'discover'} onClick={() => setViewMode('discover')}><RoundIcon><HomeRoundedIcon /></RoundIcon><span>{t('events.discover')}</span></PanelItem>
                  <PanelItem $active={viewMode === 'interested'} onClick={() => setViewMode('interested')}><RoundIcon><StarBorderRoundedIcon /></RoundIcon><span>{t('events.interestedView')}</span></PanelItem>
                </PanelNav>
                <CreateButton type='button' onClick={handleCreateEvent}><AddIcon /> {t('events.create')}</CreateButton>
                <PanelSection><h2>{t('events.format')}</h2>
                  <PanelItem $active={eventFormat === 'all'} onClick={() => setEventFormat('all')}><RoundIcon><EventAvailableOutlinedIcon /></RoundIcon><span>{t('events.all')}</span></PanelItem>
                  <PanelItem $active={eventFormat === 'physical'} onClick={() => setEventFormat('physical')}><RoundIcon><PlaceOutlinedIcon /></RoundIcon><span>{t('events.inPerson')}</span></PanelItem>
                  <PanelItem $active={eventFormat === 'online'} onClick={() => setEventFormat('online')}><RoundIcon><PublicOutlinedIcon /></RoundIcon><span>{t('events.online')}</span></PanelItem>
                </PanelSection>
                <PanelSection><h2>{t('events.date')}</h2>{dateOptions.map((value) => <TextFilter key={value} $active={dateFilter === value} onClick={() => setDateFilter(value)}>{t(`events.dates.${value}`)}</TextFilter>)}</PanelSection>
                {eventFormat !== 'online' ? (
                  <PanelSection><h2>{t('events.location')}</h2><LocationForm onSubmit={handleLocationSearch}><input ref={locationInputRef} value={locationSearch} onChange={(event) => setLocationSearch(event.target.value)} placeholder={t('events.locationPlaceholder')} /><button type='submit' disabled={isSearchingLocation}>{t(isSearchingLocation ? 'events.finding' : 'events.searchButton')}</button></LocationForm>
                    {searchedLocation && <LocationResult>{t('events.near', { location: searchedLocation.label })}<button type='button' onClick={() => { setSearchedLocation(null); setLocationSearch('') }}>{t('events.useMyLocation')}</button></LocationResult>}
                    {locationError && <ErrorText>{t(`errors.${locationError}`)}</ErrorText>}
                    <DistanceLabel>{t('events.distance')}<select value={radiusMiles} onChange={(event) => setRadiusMiles(Number(event.target.value))}>{[10, 25, 50, 100, 250].map((miles) => <option key={miles} value={miles}>{t('events.miles', { count: miles })}</option>)}</select></DistanceLabel>
                  </PanelSection>
                ) : <OnlineNotice>{t('events.onlineNotice')}</OnlineNotice>}
              </ControlPanel>

              <ResultsPanel>
                <ResultsHeader><div><h1>{t(viewMode === 'interested' ? 'events.interestedTitle' : 'events.discover')}</h1><p>{isExploreLoading ? t('events.findingEvents') : t('events.upcoming', { count: visibleEvents.length })}</p></div>
                  <QuickFilters>{eventFormat !== 'online' && <QuickButton type='button' onClick={() => locationInputRef.current?.focus()}><PlaceOutlinedIcon /> {t(searchedLocation ? 'events.selectedLocation' : 'events.myLocation')}</QuickButton>}
                    <QuickSelect value={dateFilter} onChange={(event) => setDateFilter(event.target.value)}>{dateOptions.map((value) => <option key={value} value={value}>{t(`events.dates.${value}`)}</option>)}</QuickSelect>
                    <QuickButton type='button' $active={eventFormat === 'online'} onClick={() => setEventFormat(eventFormat === 'online' ? 'all' : 'online')}><PublicOutlinedIcon /> {t('events.online')}</QuickButton>
                  </QuickFilters>
                </ResultsHeader>
                {!coordinates && eventFormat !== 'online' && <LocationNotice>{t('events.locationNotice')}</LocationNotice>}
                {isExploreLoading ? <Empty><h2>{t('events.findingNearby')}</h2></Empty> : isExploreError ? <Empty><h2>{t('errors.load')}</h2><p>{exploreMessage}</p></Empty> : visibleEvents.length === 0 ? <Empty><CalendarMonthOutlinedIcon /><h2>{t('events.noMatches')}</h2><p>{t('events.noMatchesHelp')}</p></Empty> : (
                  <Grid>
                    {visibleEvents.map((item) => (
                      <Card key={item.id} onClick={() => navigate(`/events/${item.id}`)}>
                        {item.imageUrl ? <Cover src={item.imageUrl} alt='' /> : <CoverPlaceholder />}
                        <CardBody>
                          <EventDate>
                            {formatExploreDate(item.schedule?.startDate, currentLocale)}
                            {item.schedule?.startTime ? ` · ${formatExploreTime(item.schedule.startTime, currentLocale)}` : ''}
                          </EventDate>
                          <EventTitle>{item.title}</EventTitle>
                          <EventInfo>
                            <InfoIcon><PlaceOutlinedIcon /></InfoIcon>
                            <InfoCopy>
                              <Meta>{formatExploreLocation(item.location)}</Meta>
                            </InfoCopy>
                          </EventInfo>
                          <CardFooter>
                            <SocialProof>{t('events.interested', { count: item.interestedCount || 0 })}{item.attendingCount > 0 ? ` · ${t('events.going', { count: item.attendingCount })}` : ''}</SocialProof>
                            <CardActions>
                              <InterestButton type='button' $active={item.isInterested} disabled={interestLoadingId === item.id} onClick={(event) => handleInterest(event, item)}>{item.isInterested ? <StarRoundedIcon /> : <StarBorderRoundedIcon />} {t('events.interestAction')}</InterestButton>
                              <ShareButton type='button' aria-label={t('events.share', { title: item.title })} onClick={(event) => handleShare(event, item)}><ShareOutlinedIcon /></ShareButton>
                            </CardActions>
                          </CardFooter>
                        </CardBody>
                      </Card>
                    ))}
                  </Grid>
                )}
              </ResultsPanel>
            </>
          ) : (
            <>
              <ControlPanel>
                <PanelHeader><h1>{t(`sections.${activeSection}`)}</h1><SearchWrap><SearchIcon /><input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder={t('search.public', { type: sectionLabel(activeSection) })} /></SearchWrap></PanelHeader>
                <DiscoveryNote><PublicOutlinedIcon /><div><strong>{t('public.title')}</strong><p>{t(activeSection === 'posts' ? 'public.postsDescription' : 'public.mediaDescription')}</p></div></DiscoveryNote>
              </ControlPanel>
              <ResultsPanel>
                <ResultsHeader><div><h1>{t('public.discover', { type: sectionLabel(activeSection) })}</h1><p>{isPublicLoading ? t('public.loading') : t('public.count', { count: visiblePublicItems.length, type: sectionLabel(activeSection) })}</p></div></ResultsHeader>
                {isPublicLoading ? <Empty><h2>{t('public.loadingType', { type: sectionLabel(activeSection) })}</h2></Empty> : publicError ? <Empty><h2>{t('errors.load')}</h2><p>{typeof publicError === 'string' ? publicError : t('errors.loadPublic', { type: sectionLabel(activeSection) })}</p></Empty> : visiblePublicItems.length === 0 ? <Empty>{activeSection === 'posts' ? <ArticleOutlinedIcon /> : <VideoLibraryOutlinedIcon />}<h2>{t('public.empty', { type: sectionLabel(activeSection) })}</h2><p>{t('public.emptyHelp')}</p></Empty> : (
                  <PublicGrid>{visiblePublicItems.map((item) => activeSection === 'posts' ? (
                    <PublicCard key={item._id} onClick={() => navigate(`/posts/${item._id}`)}>{item.hasImage && item.image?.url && <PublicCover src={item.image.url} alt='' />}<PublicCardBody><CreatorLine>{item.creator?.profileImg?.url && <img src={item.creator.profileImg.url} alt='' />}<span>{item.creator?.fullName || t('public.memberFallback')}</span></CreatorLine><h2>{item.postType || t('public.postFallback')}</h2><PublicText>{item.postText?.text}</PublicText><SocialProof>{t('public.likes', { count: item.likes?.length || 0 })} · {t('public.comments', { count: item.comments?.length || 0 })}</SocialProof></PublicCardBody></PublicCard>
                  ) : (
                    <PublicCard as='a' key={item._id} href={item.videoUrl} target='_blank' rel='noreferrer'><MediaCover>{item.imageUrl || item.thumbnail?.url ? <PublicCover src={item.imageUrl || item.thumbnail.url} alt='' /> : <CoverPlaceholder />}<PlayBadge><VideoLibraryOutlinedIcon /></PlayBadge></MediaCover><PublicCardBody><CreatorLine>{item.creator?.profileImg?.url && <img src={item.creator.profileImg.url} alt='' />}<span>{item.creator?.fullName || t('public.communityFallback')}</span></CreatorLine><h2>{item.title || t('public.videoFallback')}</h2><SocialProof>{t('public.views', { count: item.views || 0 })}</SocialProof></PublicCardBody></PublicCard>
                  ))}</PublicGrid>
                )}
              </ResultsPanel>
            </>
          )}
        </ExploreContentRow>
      </ExploreWorkspace>
      {viewEventModal && <CreateEventModal />}
    </PageContainerMain>
  )
}

const ExploreWorkspace = styled.div`
  flex: 1; min-width: 0; max-width: 1380px; height: 100%; display: flex; flex-direction: column; gap: 12px;
  @media (max-width: 900px) { overflow-y: auto; }
`
const ExploreHeader = styled.header`
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 24px;
  padding: 16px 20px; border-radius: 20px; background: white; color: var(--lightThemeFontColor);
  box-shadow: 0 1px 2px rgba(0, 0, 0, .05);
  h1 { margin: 0; font-size: 25px; } p { margin: 4px 0 0; color: var(--dFontColor); font-size: 13px; }
  @media (max-width: 700px) { align-items: stretch; flex-direction: column; gap: 12px; padding: 14px; }
`
const ExploreTabs = styled.nav`
  display: flex; align-items: center; gap: 7px; padding: 4px; border-radius: 12px; background: #eef1f4;
`
const ExploreTab = styled.button`
  min-width: 105px; height: 42px; display: flex; align-items: center; justify-content: center; gap: 7px;
  border: 0; border-radius: 9px; cursor: pointer; font-weight: 700;
  color: ${({ $active }) => $active ? 'white' : '#455055'};
  background: ${({ $active }) => $active ? 'var(--themeColor)' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 2px 8px rgba(0, 180, 204, .24)' : 'none'};
  svg { font-size: 20px; }
  @media (max-width: 450px) { min-width: 0; flex: 1; padding: 0 8px; }
`
const ExploreContentRow = styled.div`
  flex: 1; min-height: 0; display: flex; gap: 15px;
  @media (max-width: 900px) { flex-direction: column; flex: 0 0 auto; }
`
const ControlPanel = styled.aside`
  flex: 0 0 300px; height: 100%; overflow-y: auto; padding: 20px 12px; border-radius: 25px;
  background: #fff; color: var(--lightThemeFontColor);
  @media (max-width: 1100px) { flex-basis: 260px; }
  @media (max-width: 900px) { width: 100%; height: auto; flex-basis: auto; overflow: visible; }
`
const PanelHeader = styled.div`padding: 0 6px 14px; h1 { margin: 0 0 12px; font-size: 25px; }`
const SearchWrap = styled.div`
  height: 40px; display: flex; align-items: center; gap: 8px; padding: 0 12px; border-radius: 20px; background: #eef1f4; color: #687176;
  svg { font-size: 19px; } input { width: 100%; border: 0; outline: 0; background: transparent; color: #303638; }
`
const PanelNav = styled.nav`padding-bottom: 8px; border-bottom: 1px solid var(--borderColor);`
const PanelItem = styled.button`
  width: 100%; min-height: 48px; display: flex; align-items: center; gap: 12px; padding: 6px 9px; border: 0; border-radius: 10px;
  background: ${({ $active }) => $active ? '#eef6f7' : 'transparent'}; color: var(--lightThemeFontColor); cursor: pointer;
  text-align: left; font-size: 14px; font-weight: 600; &:hover { background: #f0f2f3; }
`
const RoundIcon = styled.span`
  width: 36px; height: 36px; flex: 0 0 36px; display: grid; place-items: center; border-radius: 50%; background: #e4e8eb;
  svg { font-size: 20px; }
`
const CreateButton = styled.button`
  width: calc(100% - 12px); height: 40px; margin: 14px 6px; display: flex; align-items: center; justify-content: center; gap: 7px;
  border: 0; border-radius: 9px; background: var(--themeColor); color: white; cursor: pointer; font-weight: 700;
`
const PanelSection = styled.section`padding: 15px 6px 8px; border-top: 1px solid var(--borderColor); h2 { margin: 0 0 8px; font-size: 15px; }`
const TextFilter = styled.button`
  width: 100%; padding: 9px 12px; border: 0; border-radius: 8px; cursor: pointer; text-align: left;
  background: ${({ $active }) => $active ? 'rgba(0, 180, 204, .12)' : 'transparent'};
  color: ${({ $active }) => $active ? 'var(--themeColor)' : 'var(--lightThemeFontColor)'}; font-weight: ${({ $active }) => $active ? 700 : 500};
  &:hover { background: #f0f2f3; }
`
const LocationForm = styled.form`
  display: grid; gap: 7px; input, button { height: 38px; border-radius: 8px; }
  input { width: 100%; border: 1px solid #d5dbde; padding: 0 10px; outline-color: var(--themeColor); }
  button { border: 0; background: #e4e8eb; color: #343a3c; cursor: pointer; font-weight: 700; }
`
const LocationResult = styled.p`
  margin: 8px 2px; color: #647074; font-size: 11px; line-height: 1.4;
  button { display: block; margin-top: 4px; padding: 0; border: 0; background: none; color: var(--themeColor); cursor: pointer; font-weight: 700; }
`
const ErrorText = styled.p`margin: 8px 2px; color: #b42318; font-size: 11px;`
const DistanceLabel = styled.label`
  display: flex; align-items: center; justify-content: space-between; margin-top: 12px; color: #5b6568; font-size: 12px; font-weight: 600;
  select { border: 1px solid #d5dbde; border-radius: 7px; padding: 7px; background: white; }
`
const OnlineNotice = styled.p`margin: 14px 6px; padding: 12px; border-radius: 10px; background: #eaf6f7; color: #526164; font-size: 12px;`
const DiscoveryNote = styled.div`
  display: flex; gap: 11px; margin: 4px 6px; padding: 14px; border-radius: 12px; background: #eaf6f7; color: #526164;
  > svg { flex: 0 0 auto; color: var(--themeColor); } strong { color: #30383b; font-size: 14px; }
  p { margin: 5px 0 0; font-size: 12px; line-height: 1.45; }
`
const ResultsPanel = styled.main`
  flex: 1; min-width: 0; height: 100%; overflow-y: auto; padding: 24px; border-radius: 25px; background: #f6f7f8; color: var(--lightThemeFontColor);
  @media (max-width: 900px) { height: auto; overflow: visible; } @media (max-width: 600px) { padding: 15px; }
`
const ResultsHeader = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 22px;
  h1 { margin: 0; font-size: 25px; } p { margin: 5px 0 0; color: var(--dFontColor); font-size: 13px; }
  @media (max-width: 700px) { flex-direction: column; }
`
const QuickFilters = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`
const QuickButton = styled.button`
  height: 38px; display: flex; align-items: center; gap: 6px; padding: 0 12px; border: 0; border-radius: 9px;
  background: ${({ $active }) => $active ? '#dcecff' : '#e3e7ea'}; color: #202426; cursor: pointer; font-weight: 700; svg { font-size: 18px; }
`
const QuickSelect = styled.select`height: 38px; padding: 0 12px; border: 0; border-radius: 9px; background: #e3e7ea; color: #202426; cursor: pointer; font-weight: 700;`
const LocationNotice = styled.p`margin: 0 0 18px; padding: 11px 13px; border-radius: 9px; background: #eaf6f7; color: #566164; font-size: 12px;`
const Grid = styled.section`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px;
  @media (max-width: 1250px) { grid-template-columns: repeat(2, minmax(0, 1fr)); } @media (max-width: 620px) { grid-template-columns: 1fr; }
`
const Card = styled.article`
  min-width: 0; overflow: hidden; display: flex; flex-direction: column; border: 1px solid #dfe3e5; border-radius: 15px; background: white; cursor: pointer;
  box-shadow: 0 2px 8px rgba(25, 38, 42, .07); transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(25, 38, 42, .12); }
`
const Cover = styled.img`width: 100%; aspect-ratio: 16 / 9; object-fit: cover; display: block; background: #dce3e5;`
const CoverPlaceholder = styled.div`width: 100%; aspect-ratio: 16 / 9; background: linear-gradient(135deg, #d8f3f4, #8fd9df);`
const CardBody = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; padding: 14px;`
const EventDate = styled.p`margin: 0 0 5px; color: var(--themeColor); font-size: 11px; font-weight: 800; letter-spacing: .01em;`
const EventTitle = styled.h2`
  display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2;
  min-height: 39px; margin: 0 0 11px; color: #202b2f; font-size: 15px; line-height: 1.3; font-weight: 750;
`
const EventInfo = styled.div`display: flex; align-items: flex-start; min-width: 0; gap: 9px;`
const InfoIcon = styled.span`
  width: 30px; height: 30px; flex: 0 0 30px; display: grid; place-items: center; border-radius: 9px;
  background: rgba(0, 180, 204, .1); color: var(--themeColor); svg { font-size: 17px; }
`
const InfoCopy = styled.div`min-width: 0; padding-top: 1px;`
const Meta = styled.p`margin: 0; color: #536065; font-size: 11px; font-weight: 650; line-height: 1.35; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const CardFooter = styled.div`margin-top: auto; padding-top: 13px;`
const SocialProof = styled.p`min-height: 16px; margin: 0 0 9px; color: #798286; font-size: 11px;`
const CardActions = styled.div`display: grid; grid-template-columns: 1fr 42px; gap: 8px;`
const InterestButton = styled.button`
  height: 38px; display: flex; align-items: center; justify-content: center; gap: 7px; border: 0; border-radius: 8px;
  background: ${({ $active }) => $active ? 'rgba(0, 180, 204, .16)' : '#e5e8eb'};
  color: ${({ $active }) => $active ? 'var(--themeColor)' : '#202426'}; cursor: pointer; font-weight: 700;
  &:disabled { opacity: .6; cursor: default; } svg { font-size: 19px; }
`
const ShareButton = styled.button`height: 38px; display: grid; place-items: center; border: 0; border-radius: 8px; background: #e5e8eb; color: #202426; cursor: pointer; svg { font-size: 19px; }`
const Empty = styled.section`
  min-height: 320px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; color: #697274;
  > svg { font-size: 48px; color: #9aa4a8; } h2 { margin: 12px 0 5px; color: #303738; } p { margin: 0; }
`
const PublicGrid = styled.section`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px;
  @media (max-width: 1250px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 620px) { grid-template-columns: 1fr; }
`
const PublicCard = styled.article`
  min-width: 0; overflow: hidden; border: 1px solid #dfe3e5; border-radius: 13px; background: white;
  color: inherit; text-decoration: none; cursor: pointer; box-shadow: 0 1px 2px rgba(0, 0, 0, .08);
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(25, 38, 42, .12); }
`
const PublicCover = styled.img`width: 100%; aspect-ratio: 16 / 9; display: block; object-fit: cover; background: #dce3e5;`
const PublicCardBody = styled.div`
  padding: 13px; h2 { margin: 10px 0 6px; color: #202426; font-size: 16px; line-height: 1.3; }
`
const CreatorLine = styled.div`
  display: flex; align-items: center; gap: 8px; color: #5d696d; font-size: 12px; font-weight: 700;
  img { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; }
`
const PublicText = styled.p`
  display: -webkit-box; margin: 0 0 10px; overflow: hidden; color: #596468; font-size: 13px; line-height: 1.45;
  -webkit-line-clamp: 4; -webkit-box-orient: vertical;
`
const MediaCover = styled.div`position: relative;`
const PlayBadge = styled.span`
  position: absolute; inset: 0; display: grid; place-items: center; color: white; background: rgba(0, 0, 0, .14);
  svg { width: 48px; height: 48px; padding: 10px; border-radius: 50%; background: rgba(0, 0, 0, .68); }
`
